import React, { useState } from 'react';
import { subcontractAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Search, Download, FileSearch, PackageCheck, Truck } from 'lucide-react';

const refTypeOptions = [
  { value: 'wo', label: 'Customer Work Order No.' },
  { value: 'dc', label: 'Customer DC No.' },
  { value: 'po', label: 'Customer PO No.' },
];

export default function TraceabilityReportPage() {
  const [refType, setRefType] = useState('wo');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);

  const generate = async () => {
    if (!value.trim()) { toast.error('Please enter a value to search'); return; }
    setLoading(true);
    try {
      const res = await subcontractAPI.traceabilityReport({ ref_type: refType, value: value.trim() });
      setReport(res.data);
      if ((res.data.receipts || []).length === 0) toast.info('No receipts found for this reference');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally { setLoading(false); }
  };

  const exportExcel = async () => {
    if (!value.trim()) { toast.error('Please enter a value first'); return; }
    setExporting(true);
    try {
      const res = await subcontractAPI.exportTraceabilityReport({ ref_type: refType, value: value.trim() });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Traceability_${refType.toUpperCase()}_${value.trim().replace(/[^A-Za-z0-9_-]+/g, '_')}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export');
    } finally { setExporting(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="traceability-report-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2">
          <FileSearch className="w-6 h-6 text-blue-600" /> Material Traceability Report
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Trace how material received against a customer WO / DC / PO was received and dispatched.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Search by</Label>
          <Select value={refType} onValueChange={(v) => { setRefType(v); }}>
            <SelectTrigger className="w-56 h-9" data-testid="trace-ref-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {refTypeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 flex-1 min-w-[220px]">
          <Label className="text-xs">Value (exact match)</Label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && generate()}
            placeholder="Enter the exact reference number"
            className="h-9"
            data-testid="trace-value-input"
          />
        </div>
        <Button onClick={generate} disabled={loading} className="h-9 gap-1.5" data-testid="trace-generate-btn">
          <Search className="w-4 h-4" /> {loading ? 'Searching...' : 'Generate'}
        </Button>
        <Button onClick={exportExcel} disabled={exporting || !report} variant="outline" className="h-9 gap-1.5 border-slate-300" data-testid="trace-export-btn">
          <Download className="w-4 h-4" /> {exporting ? 'Preparing...' : 'Export Excel'}
        </Button>
      </div>

      {report && (
        <div className="space-y-3" data-testid="trace-report-result">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="text-slate-600">{report.ref_label}: <strong className="text-[#0F172A]">{report.value}</strong></span>
            <span className="text-slate-600 flex items-center gap-1"><PackageCheck className="w-4 h-4 text-emerald-600" /> Receipts: <strong>{report.summary.receipt_count}</strong></span>
            <span className="text-slate-600">Total Received: <strong>{report.summary.total_received}</strong></span>
            <span className="text-slate-600 flex items-center gap-1"><Truck className="w-4 h-4 text-blue-600" /> Total Dispatched: <strong>{report.summary.total_dispatched}</strong></span>
            <span className="text-slate-600">Balance (with us): <strong className={report.summary.total_balance > 0 ? 'text-amber-600' : 'text-[#0F172A]'}>{report.summary.total_balance}</strong></span>
          </div>

          <div className="border border-slate-200 rounded-md overflow-x-auto bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs">Inbound Challan</TableHead>
                  <TableHead className="text-xs">Receipt Date</TableHead>
                  <TableHead className="text-xs">Part Name</TableHead>
                  <TableHead className="text-xs">Cust WO</TableHead>
                  <TableHead className="text-xs">Cust DC</TableHead>
                  <TableHead className="text-xs">Cust PO</TableHead>
                  <TableHead className="text-xs text-right">Qty Rcvd</TableHead>
                  <TableHead className="text-xs text-right">Balance</TableHead>
                  <TableHead className="text-xs">Dispatch DC</TableHead>
                  <TableHead className="text-xs">Disp. Item</TableHead>
                  <TableHead className="text-xs">Disp. Date</TableHead>
                  <TableHead className="text-xs">Invoice</TableHead>
                  <TableHead className="text-xs text-right">Qty Disp.</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.receipts.length === 0 ? (
                  <TableRow><TableCell colSpan={13} className="text-center py-8 text-sm text-slate-400">No matching receipts</TableCell></TableRow>
                ) : report.receipts.map((rc, ri) => {
                  const rows = rc.dispatches.length ? rc.dispatches : [null];
                  return rows.map((d, di) => (
                    <TableRow key={`${ri}-${di}`} data-testid={`trace-row-${ri}-${di}`} className={di === 0 ? 'border-t-2 border-slate-200' : ''}>
                      <TableCell className="text-xs">{di === 0 ? rc.inbound_challan_number : ''}</TableCell>
                      <TableCell className="text-xs">{di === 0 ? rc.receipt_date : ''}</TableCell>
                      <TableCell className="text-xs">{di === 0 ? rc.part_name : ''}</TableCell>
                      <TableCell className="text-xs font-mono">{di === 0 ? rc.customer_wo : ''}</TableCell>
                      <TableCell className="text-xs">{di === 0 ? rc.customer_dc : ''}</TableCell>
                      <TableCell className="text-xs">{di === 0 ? rc.customer_po : ''}</TableCell>
                      <TableCell className="text-xs text-right font-medium">{di === 0 ? rc.qty_received : ''}</TableCell>
                      <TableCell className="text-xs text-right font-medium">{di === 0 ? <span className={rc.balance > 0 ? 'text-amber-600' : 'text-slate-500'}>{rc.balance}</span> : ''}</TableCell>
                      {d ? (
                        <>
                          <TableCell className="text-xs font-mono">{d.dc_number}</TableCell>
                          <TableCell className="text-xs">{d.dispatch_date}</TableCell>
                          <TableCell className="text-xs">
                            {d.invoice_number
                              ? <span className="text-emerald-700">{d.invoice_number}</span>
                              : <span className="text-amber-600">Pending</span>}
                          </TableCell>
                          <TableCell className="text-xs text-right">{d.qty_dispatched}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${d.return_as_is ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                              {d.return_as_is ? 'Return-as-is' : 'Dispatched'}
                            </Badge>
                          </TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={6} className="text-xs text-slate-400 italic">Not dispatched</TableCell>
                      )}
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
